import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { AppearWithHeight } from '@y42/animation';
import { Y42FeatureTypeToLogo } from '@y42/feature';
import { modelIcon } from '@y42/feature/icons';
import { Y42FeatureType } from '@y42/json-schema';
import { combineLatest, map, of, switchMap } from 'rxjs';
import {
  OrchestrationSupportedEntityTypeEnum,
  OrchestrationTablesPickerPopupService,
} from './orchestration-tables-picker-popup.service';

interface OrchestrationTablesPickerPopupTab {
  name: OrchestrationSupportedEntityTypeEnum;
  logo: string;
}

export interface OrchestrationTablesPickerPopupInput {
  alreadySelectedAssets: string[];
  allowDependencies: boolean;
}

export interface OrchestrationTablesPickerPopupOutput {
  selectedAssets: string[];
  withDependencies: boolean;
}

@UntilDestroy()
@Component({
  selector: 'y42-orchestration-tables-picker-popup',
  templateUrl: './orchestration-tables-picker-popup.component.html',
  styleUrls: ['./orchestration-tables-picker-popup.component.scss'],
  animations: [AppearWithHeight],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [OrchestrationTablesPickerPopupService],
})
export class OrchestrationTablesPickerPopupComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: OrchestrationTablesPickerPopupInput,
    private readonly dialogRef: MatDialogRef<
      OrchestrationTablesPickerPopupComponent,
      OrchestrationTablesPickerPopupOutput
    >,
    private readonly orchestrationDynamicTablesPickerPopupService: OrchestrationTablesPickerPopupService,
  ) {}

  readonly trackByName = getTrackByFn<OrchestrationTablesPickerPopupTab>('name');

  readonly tabs: OrchestrationTablesPickerPopupTab[] = [
    {
      name: OrchestrationSupportedEntityTypeEnum.Integrations,
      logo: Y42FeatureTypeToLogo[Y42FeatureType.Integration],
    },
    {
      name: OrchestrationSupportedEntityTypeEnum.Models,
      logo: modelIcon,
    },
    {
      name: OrchestrationSupportedEntityTypeEnum.Exports,
      logo: Y42FeatureTypeToLogo[Y42FeatureType.Export],
    },
  ];

  private readonly selectedAssets$ = combineLatest([
    this.orchestrationDynamicTablesPickerPopupService.integrationPaths$,
    this.orchestrationDynamicTablesPickerPopupService.modelPaths$,
    this.orchestrationDynamicTablesPickerPopupService.exportPaths$,
  ]).pipe(map(([integrations, models, exports]) => [...integrations, ...models, ...exports]));

  private readonly duplicateSelectedItems$ = this.selectedAssets$.pipe(
    map((selectedAssets) => selectedAssets.filter((id) => this.dialogData.alreadySelectedAssets.includes(id))),
  );

  readonly state = connectState(this, {
    activeTabIndex: this.orchestrationDynamicTablesPickerPopupService.activeAssetType$.pipe(
      map((activeTabName) => this.tabs.findIndex((t) => t.name === activeTabName)),
    ),
    enableDatasourceSelector: this.orchestrationDynamicTablesPickerPopupService.activeAssetType$.pipe(
      map((activeAssetType) =>
        [OrchestrationSupportedEntityTypeEnum.Integrations, OrchestrationSupportedEntityTypeEnum.Models].includes(
          activeAssetType,
        ),
      ),
    ),
    enableExportSelector: this.orchestrationDynamicTablesPickerPopupService.activeAssetType$.pipe(
      map((activeAssetType) => [OrchestrationSupportedEntityTypeEnum.Exports].includes(activeAssetType)),
    ),
    selectedDatasourcePaths: this.orchestrationDynamicTablesPickerPopupService.activeAssetType$.pipe(
      switchMap((activeAssetType) => {
        if (activeAssetType === OrchestrationSupportedEntityTypeEnum.Integrations) {
          return this.orchestrationDynamicTablesPickerPopupService.integrationPaths$;
        } else if (activeAssetType === OrchestrationSupportedEntityTypeEnum.Models) {
          return this.orchestrationDynamicTablesPickerPopupService.modelPaths$;
        }
        return of([]);
      }),
    ),
    selectedExportPaths: this.orchestrationDynamicTablesPickerPopupService.exportPaths$,
    selectedAssets: this.selectedAssets$,
    isSelectionEnabled: combineLatest([this.selectedAssets$, this.duplicateSelectedItems$]).pipe(
      map(([selectedItems, duplicates]) => selectedItems.length > 0 && duplicates.length === 0),
    ),
    allowSelectionWithDependencies: this.orchestrationDynamicTablesPickerPopupService.activeAssetType$.pipe(
      map(
        (activeAssetType) =>
          this.dialogData.allowDependencies &&
          [OrchestrationSupportedEntityTypeEnum.Models, OrchestrationSupportedEntityTypeEnum.Exports].includes(
            activeAssetType,
          ),
      ),
    ),
    numberOfDuplicates: this.duplicateSelectedItems$.pipe(map((duplicateIds) => duplicateIds.length)),
  });

  changeTab(index: number): void {
    const activeTab = this.tabs[index];
    this.orchestrationDynamicTablesPickerPopupService.setActiveAssetType(activeTab.name);
  }

  datasourcePathsChanged(datasourcePaths: string[]): void {
    const currentAssetType = this.orchestrationDynamicTablesPickerPopupService.getActiveAssetType();
    if (currentAssetType === OrchestrationSupportedEntityTypeEnum.Integrations) {
      this.orchestrationDynamicTablesPickerPopupService.setSelectedIntegrations(datasourcePaths);
    } else if (currentAssetType === OrchestrationSupportedEntityTypeEnum.Models) {
      this.orchestrationDynamicTablesPickerPopupService.setSelectedModels(datasourcePaths);
    }
  }

  exportPathsChanged(exportPaths: string[]): void {
    this.orchestrationDynamicTablesPickerPopupService.setSelectedExports(exportPaths);
  }

  selectAssets(withDependencies = false): void {
    const selectedAssets = this.state.selectedAssets;
    if (!selectedAssets) {
      return;
    }

    this.dialogRef.close({ selectedAssets, withDependencies });
  }
}
